import React, { useRef, useEffect } from 'react';
import Message from './Message';
import TypingIndicator from './TypingIndicator';
import SuggestedChips from './SuggestedChips';
import type { Message as MessageType } from '../../hooks/useChat';

interface MessageListProps {
  messages: MessageType[];
  isTyping: boolean;
  streamingMessageId?: string | null;
  isExpanded?: boolean;
  onSuggestedClick: (question: string) => void;
}

// Matches the panel's expand/collapse transition in ChatWidget.
const RESIZE_SETTLE_MS = 320;

const MessageList: React.FC<MessageListProps> = ({ messages, isTyping, streamingMessageId, isExpanded = false, onSuggestedClick }) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const showSuggestedChips = messages.length === 1 && messages[0].id === 'welcome';

  useEffect(() => {
    // Skip on the fresh welcome-only screen: there's nothing below the fold yet,
    // and scrolling here just clips the top of the greeting bubble.
    if (showSuggestedChips) {
      return;
    }
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping, showSuggestedChips]);

  // Expanding or collapsing changes this container's height, which the effect
  // above never sees — it only watches the message data. Without this the
  // thread is left parked mid-scroll after a toggle. Wait out the panel's
  // transition first: scrolling into the pre-resize layout lands in the wrong
  // place, and 'auto' avoids racing a smooth scroll against the resize.
  useEffect(() => {
    if (showSuggestedChips) {
      return;
    }
    const timer = setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
    }, RESIZE_SETTLE_MS);

    return () => clearTimeout(timer);
  }, [isExpanded, showSuggestedChips]);

  return (
    <div className="flex-1 overflow-y-auto p-4 bg-dev-bg-light scrollbar-thin">
      <div className="max-w-3xl mx-auto">
        {messages.map((message) => (
          <Message key={message.id} message={message} isStreaming={message.id === streamingMessageId} />
        ))}
        {isTyping && <TypingIndicator />}
        {showSuggestedChips && !isTyping && (
          <SuggestedChips onChipClick={onSuggestedClick} />
        )}
        <div ref={messagesEndRef} />
      </div>
    </div>
  );
};

export default MessageList;
